import { type Decimal, differsBeyondTolerance } from '@insio/decimal';
import type { InvoiceLine, InvoiceLineAmounts } from '../../../../graphql/generated/graphql';

export type ComputableField = 'unitPrice' | 'vatAmount' | 'totalAmountNoVat' | 'totalAmountInclVat';

const COMPARED_FIELDS: readonly ComputableField[] = [
  'unitPrice',
  'vatAmount',
  'totalAmountNoVat',
  'totalAmountInclVat'
];

/** VAT-only fields — meaningless on non-VAT documents, where they are hidden. */
const VAT_ONLY_FIELDS: ReadonlySet<ComputableField> = new Set(['vatAmount', 'totalAmountInclVat']);

/** Fields the result may suggest given the typed inputs: net total from qty × price, a blank unit
 * price from a non-zero qty and typed net total, VAT fields from any net base. */
export function derivableFields(line: Partial<InvoiceLine> | undefined): Set<ComputableField> {
  const out = new Set<ComputableField>();
  if (!line) return out;
  const hasQuantity = line.quantity != null;
  const forwardBase = hasQuantity && line.unitPrice != null;
  const baseGrounded = forwardBase || line.totalAmountNoVat != null;

  if (forwardBase) out.add('totalAmountNoVat');
  if (baseGrounded) {
    out.add('vatAmount');
    out.add('totalAmountInclVat');
  }
  if (
    hasQuantity &&
    !line.quantity!.isZero() &&
    line.totalAmountNoVat != null &&
    line.unitPrice == null
  )
    out.add('unitPrice');
  return out;
}

/**
 * Fields whose computed value should surface as a hint: derivable, present in
 * `computed`, and either blank or disagreeing with the typed value (sign ignored).
 * Returns an empty set when `computed` is `null`. On non-VAT documents the
 * VAT-only fields (`vatAmount`, `totalAmountInclVat`) are never reported.
 */
export function computeMismatchFields(
  computed: InvoiceLineAmounts | null,
  line: Partial<InvoiceLine> | undefined,
  isVatDocument: boolean
): Set<ComputableField> {
  const out = new Set<ComputableField>();
  if (!computed) return out;
  const derivable = derivableFields(line);
  for (const f of COMPARED_FIELDS) {
    if (!derivable.has(f)) continue;
    if (!isVatDocument && VAT_ONLY_FIELDS.has(f)) continue;
    const expected = computed[f] as Decimal | null | undefined;
    if (expected == null) continue;
    const typed = line?.[f] as Decimal | null | undefined;
    if (typed == null) {
      out.add(f);
      continue;
    }
    if (differsBeyondTolerance(expected, typed, { ignoreSign: true })) out.add(f);
  }
  return out;
}
